import {
  Image,
  Linking,
  Platform,
  Share,
  TouchableOpacity,
  View,
  TextProps,
  Text,
  StyleSheet,
} from 'react-native'
import {
  type ComponentProps,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import LinkIcon from '@/src/assets/icons/LinkIcon'
import ShareIcon from '@/src/assets/icons/ShareIcon'
import EmailIcon from '@/src/assets/icons/EmailIcon'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import FontAwesome from '@expo/vector-icons/FontAwesome'
import FontAwesome5 from '@expo/vector-icons/FontAwesome5'
import FontAwesome6 from '@expo/vector-icons/FontAwesome6'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import * as CardApplication from '@/src/experiences/card-application'
import { triggerSegmentAction } from '@/src/services/segment/triggerSegmentEvents'
import { copyToClipboard } from '@/src/common/utils/copyToClipboard'
import { useReferralParameterStore } from '@/src/services/Statsig/useReferralParameterStore'
import {
  composeAsync as composeEmailAsync,
  isAvailableAsync as isEmailAvailableAsync,
} from 'expo-mail-composer'
import { sendSMSAsync, isAvailableAsync as isSMSAvailableAsync } from 'expo-sms'
import { ContactPicker } from '@/src/app/main/refer-a-friend/contactPicker'
import { useReferralShareMessage } from './_useReferralShareMessage'
import { useCardReferralCode } from '@/src/common/hooks/useCardReferralCode'
import { Button } from '@/src/common/components/Button'
import { LinearGradient } from 'expo-linear-gradient'
import { isAvailableAsync as isContactsAvailableAsync } from 'expo-contacts'
import BottomSheet from '@gorhom/bottom-sheet'
import { useReferralStats } from '@/src/common/hooks/useReferralStats'
import { maxWidth } from '@/src/common/wrappers/MainWrapper'
import { StandaloneWrapper } from '@/src/common/wrappers/StandaloneWrapper'
import { DesignSystem } from '@/src/design-system'
import { DuplicateIcon } from '@/src/assets/icons/Duplicate'
import { mesaColorNames, typography } from '@/src/common/theme'
import * as Surveys from '@/src/experiences/surveys'

export function ReferAFriendHeader({
  textContainerProps,
  headerTextStyle,
  subheaderTextStyle,
}: {
  imageProps?: ComponentProps<typeof Image>
  textContainerProps?: PropsWithChildren<ComponentProps<typeof View>>
  headerTextStyle?: TextProps['style']
  subheaderTextStyle?: TextProps['style']
}) {
  const referralParameterStore = useReferralParameterStore()

  return (
    <>
      <View {...textContainerProps} style={[styles.headerTextContainer, textContainerProps?.style]}>
        <Text style={[styles.headerText, headerTextStyle]}>
          Refer a friend and get{' '}
          {referralParameterStore
            .get('referrer_points_per_card_referral', Number.NaN)
            .toLocaleString()}{' '}
          Mesa Points!
        </Text>
        <Text style={[styles.subheaderText, subheaderTextStyle]}>
          When you refer a friend to the Mesa Homeowners Card, you both get{' '}
          {referralParameterStore
            .get('referee_points_per_card_referral', Number.NaN)
            .toLocaleString()}{' '}
          Mesa Points!
        </Text>
        {textContainerProps?.children}
      </View>
    </>
  )
}

interface ActionItemOption {
  name: string
  icon: React.ReactNode
  onPress: () => void
  shouldShow: () => boolean | Promise<boolean>
}

export default function ReferAFriend() {
  const referralCode = useCardReferralCode()
  const { shareSheetLink, shareMessageWithCode } = useReferralShareMessage(referralCode)
  const referralParameterStore = useReferralParameterStore()

  const contactPickerRef = useRef<BottomSheet>(null)
  const [isContactsAvailable, setIsContactsAvailable] = useState(false)
  const [availableActions, setAvailableActions] = useState<ActionItemOption[]>([])
  const [didCopyReferralCode, setDidCopyReferralCode] = useState(false)
  const [didShareReferralCode, setDidShareReferralCode] = useState(false)
  Surveys.Hooks.useScreenUnmountTrigger('rewards_referral_code_copied', didCopyReferralCode)
  Surveys.Hooks.useScreenUnmountTrigger('rewards_referral_shared', didShareReferralCode)

  const { data: referralStatsData } = useReferralStats()

  const cardPolicyAndProcedures =
    CardApplication.Statsig.ParameterStores.useCardPolicyAndProcedures()
  const launchTerms = () =>
    void openInAppBrowser(
      cardPolicyAndProcedures.get(
        'mesa_refer_a_cardholder_terms_and_conditions',
        'http://www.mesamember.com/legal/mesa-refer-a-friend-terms-and-conditions',
      ),
      'Refer a Friend Terms and Conditions',
    )

  const launchSweepstakesTerms = () =>
    void openInAppBrowser(
      'https://www.mesamember.com/legal/mortgage-giveaway-referral-sweepstakes-official-rules?lid=4sqygnuz34md',
      'Sweepstakes Terms and Conditions',
    )

  const numberOfReferredMembers =
    referralStatsData?.getReferralStats.CARD.numberOfReferredMembers ?? 0
  const pointsEarned = referralStatsData?.getReferralStats.CARD.pointsEarned ?? 0
  const urlEncodedShareMessageWithCode = encodeURIComponent(shareMessageWithCode)
  const shareMessageWithCodeAndLink = `${shareMessageWithCode} ${shareSheetLink}`
  const urlEncodedShareMessageWithCodeAndLink = encodeURIComponent(shareMessageWithCodeAndLink)

  const actionItemOptions = useMemo(
    () =>
      [
        {
          name: 'Copy',
          icon: (
            <View style={styles.actionItemIcon}>
              <LinkIcon
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
                width={30}
                height={30}
              />
            </View>
          ),
          onPress: () => {
            void copyToClipboard('Referral code', shareMessageWithCodeAndLink)
            setDidCopyReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Copy Referral Code Button',
            })
          },
          shouldShow: () => true,
        },
        {
          name: 'Share',
          icon: (
            <View style={styles.actionItemIcon}>
              <ShareIcon
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
                width={30}
                height={30}
              />
            </View>
          ),
          onPress: () => {
            void Share.share({
              message: shareMessageWithCode,
              url: shareSheetLink,
            })
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button',
            })
          },
          shouldShow: () => true,
        },
        {
          name: 'Email',
          icon: (
            <View style={styles.actionItemIcon}>
              <EmailIcon
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
                width={30}
                height={30}
              />
            </View>
          ),
          onPress: () => {
            void composeEmailAsync({
              subject: 'Mesa Homeowners Card Referral',
              body: shareMessageWithCodeAndLink,
            })
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with Email',
            })
          },
          shouldShow: isEmailAvailableAsync,
        },
        {
          name: 'WhatsApp',
          icon: (
            <View style={[styles.actionItemIcon, { backgroundColor: '#25D366' }]}>
              <FontAwesome
                name="whatsapp"
                size={30}
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
              />
            </View>
          ),
          onPress: () => {
            void Linking.openURL(`https://wa.me/?text=${urlEncodedShareMessageWithCodeAndLink}`)
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with WhatsApp',
            })
          },
          shouldShow: () =>
            Linking.canOpenURL(`https://wa.me/?text=${urlEncodedShareMessageWithCodeAndLink}`),
        },
        {
          name: 'Messages',
          icon: (
            <LinearGradient
              colors={['#67FF81', '#01B41F']}
              start={{ x: 0, y: 0 }}
              end={{ x: 0, y: 1 }}
              style={styles.actionItemIcon}
            >
              <MaterialCommunityIcons
                name="chat"
                size={30}
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
              />
            </LinearGradient>
          ),
          onPress: () => {
            void sendSMSAsync([], shareMessageWithCodeAndLink)
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with Messages',
            })
          },
          shouldShow: isSMSAvailableAsync,
        },
        {
          name: 'X',
          icon: (
            <View style={[styles.actionItemIcon, { backgroundColor: '#1C1C1B' }]}>
              <FontAwesome6
                name="x-twitter"
                size={28}
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
              />
            </View>
          ),
          onPress: () => {
            void Linking.openURL(
              `https://x.com/intent/post?text=${urlEncodedShareMessageWithCode}&url=${shareSheetLink}`,
            )
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with X',
            })
          },
          shouldShow: () =>
            Linking.canOpenURL(
              `https://x.com/intent/post?text=${urlEncodedShareMessageWithCode}&url=${shareSheetLink}`,
            ),
        },
        {
          name: 'Messenger',
          icon: (
            <LinearGradient
              colors={['#0498FA', '#5C5EFE', '#F44E90', '#FA8679']}
              start={{ x: 0, y: 1 }}
              end={{ x: 1, y: 0 }}
              style={styles.actionItemIcon}
            >
              <FontAwesome5
                name="facebook-messenger"
                size={28}
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
              />
            </LinearGradient>
          ),
          onPress: () => {
            void Linking.openURL(`fb-messenger://share?link=${shareSheetLink}`)
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with Messenger',
            })
          },
          shouldShow: () =>
            Platform.OS === 'web'
              ? false
              : Linking.canOpenURL(`fb-messenger://share?link=${shareSheetLink}`),
        },
        {
          name: 'Reddit',
          icon: (
            <View style={[styles.actionItemIcon, { backgroundColor: '#FF4500' }]}>
              <FontAwesome5
                name="reddit-alien"
                size={28}
                color={mesaColorNames['Mesa - Brand/White #FFFFFF']}
              />
            </View>
          ),
          onPress: () => {
            void Linking.openURL(
              `https://www.reddit.com/submit?url=${shareSheetLink}&title=${urlEncodedShareMessageWithCode}`,
            )
            setDidShareReferralCode(true)
            void triggerSegmentAction({
              component: 'Button',
              action: 'Pressed Share Referral Code Button with Reddit',
            })
          },
          shouldShow: () =>
            Linking.canOpenURL(
              `https://www.reddit.com/submit?url=${shareSheetLink}&title=${urlEncodedShareMessageWithCode}`,
            ),
        },
      ] satisfies ActionItemOption[],
    [
      shareMessageWithCode,
      shareSheetLink,
      shareMessageWithCodeAndLink,
      urlEncodedShareMessageWithCodeAndLink,
      urlEncodedShareMessageWithCode,
    ],
  )

  useEffect(() => {
    async function fetchAvailableActions() {
      const availableActions = (
        await Promise.all(
          referralParameterStore.get('actions', ['Share', 'Copy']).flatMap(async (action) => {
            const actionOption = actionItemOptions.find((option) => option.name === action)
            if (actionOption && (await actionOption.shouldShow())) {
              return [actionOption]
            }
            return []
          }),
        )
      )
        .flat()
        .slice(0, 4)
      setAvailableActions(availableActions)
    }
    void fetchAvailableActions()
  }, [actionItemOptions, referralParameterStore])

  useEffect(() => {
    async function checkContactsAvailability() {
      setIsContactsAvailable(await isContactsAvailableAsync())
    }
    void checkContactsAvailability()
  }, [])

  return (
    <>
      <StandaloneWrapper screen="/main/refer-a-friend" title="Refer a friend" showProfile={false}>
        <View style={styles.headerContainer}>
          <ReferAFriendHeader />
        </View>
        <TouchableOpacity
          onPress={() => {
            void copyToClipboard('Referral code', referralCode)
            setDidCopyReferralCode(true)
          }}
          activeOpacity={0.7}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
          }}
        >
          <Text style={styles.referralCodeText} selectable={true}>
            {referralCode}
          </Text>
          <DuplicateIcon style={{ marginLeft: 8 }} color={styles.referralCodeText.color} />
        </TouchableOpacity>

        <View style={styles.actionContainer}>
          {availableActions.map(({ name, icon, onPress }) => (
            <View key={name} style={styles.actionItem}>
              <TouchableOpacity style={styles.actionItemIcon} onPress={onPress}>
                {icon}
              </TouchableOpacity>
              <Text style={styles.actionItemText}>{name}</Text>
            </View>
          ))}
        </View>

        {isContactsAvailable && (
          <View style={styles.contactPickerCTAContainer}>
            <View style={styles.headerTextContainer}>
              <Text style={styles.headerText}>Connect your contacts</Text>
              <Text style={styles.subheaderText}>Invite friends with one click</Text>
            </View>
            <Button
              title="Get Contacts"
              onPress={() => {
                contactPickerRef.current?.snapToIndex(0)
              }}
            />
          </View>
        )}

        <DesignSystem.Card style={[styles.commonCard, styles.infoCard]}>
          <View style={styles.pointsContainer}>
            <Text style={typography['label/sm-bold']}>Referral Mesa Points Earned</Text>
            <Text style={typography['heading/heading1']}>{pointsEarned.toLocaleString()}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoRowHeader}>Referred Mesa Cardmembers</Text>
            <Text style={typography['label/sm-bold']}>
              {numberOfReferredMembers.toLocaleString()}
            </Text>
          </View>
        </DesignSystem.Card>

        <DesignSystem.Card style={styles.commonCard}>
          {Object.entries(referralParameterStore.get('faqs', {})).map(([title, content], index) => (
            <DesignSystem.Accordion trigger={title} index={index} key={title}>
              <DesignSystem.Markdown
                style={{
                  body: {
                    ...typography['paragraph/sm'],
                    color: mesaColorNames['Mesa - Brand/Blackish #222222'],
                  },
                }}
                children={content.join('')}
              />
            </DesignSystem.Accordion>
          ))}
        </DesignSystem.Card>

        <Text style={[styles.termsTextContainer, typography.S]}>
          Refer a Friend{' '}
          <Text style={styles.termsText} onPress={launchTerms}>
            Terms and Conditions
          </Text>
          .
        </Text>
        <Text style={[styles.termsTextContainer, typography.S]}>
          Sweepstakes Terms and Conditions{' '}
          <Text style={styles.termsText} onPress={launchSweepstakesTerms}>
            Terms and Conditions
          </Text>
          .
        </Text>
      </StandaloneWrapper>
      <ContactPicker bottomSheetRef={contactPickerRef} />
    </>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: mesaColorNames['Mesa - Brand/Secondary #223843'],
    gap: 8,
    color: mesaColorNames['global/grey/80 #2F3133'],
  },
  scrollView: {
    gap: 32,
    maxWidth,
    marginHorizontal: 'auto',
    width: '100%',
  },
  headerContainer: {
    alignItems: 'center',
    gap: 24,
  },
  headerTextContainer: {
    gap: 8,
  },
  headerText: {
    ...typography['heading/heading3'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    textAlign: 'center',
  },
  subheaderText: {
    ...typography['paragraph/sm'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    textAlign: 'center',
  },
  commonCard: {
    borderRadius: 12,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  infoCard: {
    gap: 24,
  },
  pointsContainer: {
    gap: 8,
  },
  infoRow: {
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignItems: 'center',
  },
  infoRowHeader: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Greyscale/500 #6E7375'],
  },
  actionContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
  },
  actionItem: {
    gap: 12,
    alignItems: 'center',
  },
  actionItemIcon: {
    width: 64,
    height: 64,
    backgroundColor: mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: mesaColorNames['Mesa - Brand/Blackish #222222'],
    shadowOffset: {
      width: 0,
      height: 0,
    },
    shadowOpacity: 0.37,
    shadowRadius: 6,
    elevation: 0,
    backdropFilter: 'blur(120px)',
    boxShadow: '0px 4px 16px 0px #00000029',
  },
  actionItemText: {
    ...typography['label/sm-bold'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  contactPickerCTAContainer: {
    gap: 16,
  },
  termsTextContainer: {
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
  },
  termsText: {
    ...typography['label/sm-regular'],
    color: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'],
  },
  referralCodeText: {
    ...typography['heading/heading1'],
    color: mesaColorNames['Mesa - Brand/White #FFFFFF'],
    textAlign: 'center',
  },
})
